import React, { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { photoService } from '../../services/entityServiceSingleton';
import { getEntityIconAndColor } from '../../utils/navigationIconUtils';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import type { EntityCardField } from './EntityCard';

const AVATAR_PALETTE = [
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    { bg: 'bg-violet-100', text: 'text-violet-700' },
    { bg: 'bg-rose-100', text: 'text-rose-700' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    { bg: 'bg-teal-100', text: 'text-teal-700' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },
];

const SUBTYPE_BADGE_STYLES: Record<string, string> = {
    employee: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    student: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    contractor: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    applicant: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
    public: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    sponsored: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    shared: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
    vacancy: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
};

function getInitials(name: string | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.[0]?.toUpperCase() || '?';
}

function hashCode(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

function resolveDisplayName(entity: any): string {
    return entity?.DisplayName || entity?.rawData?.DisplayName || entity?.Name || entity?.rawData?.Name || 'Unnamed';
}

function resolveId(entity: any): string {
    const id = entity?.Id || entity?.rawData?.Id;
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id.value) return id.value;
    return String(id);
}

function resolveEntityType(entity: any): string {
    if (typeof entity?.EntityType === 'string') return entity.EntityType;
    if (entity?.EntityType && typeof entity.EntityType === 'object') {
        return (entity.EntityType as any).DisplayName || (entity.EntityType as any).Id || '';
    }
    return '';
}

export interface ShadcnEntityCardProps {
    entity: any;
    entityType: string;
    onSelect: (entity: any) => void;
    isPerson?: boolean;
    selectedEntityType?: string;
    visibleFields?: Set<string>;
    fields?: EntityCardField[];
}

const ShadcnEntityCard: React.FC<ShadcnEntityCardProps> = ({
    entity,
    entityType,
    onSelect,
    isPerson = false,
    selectedEntityType,
    visibleFields,
    fields,
}) => {
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [photoLoaded, setPhotoLoaded] = useState(!isPerson);

    const displayName = resolveDisplayName(entity);
    const entityId = resolveId(entity);
    const initials = getInitials(displayName);
    const palette = AVATAR_PALETTE[hashCode(displayName) % AVATAR_PALETTE.length];

    const resolvedType = resolveEntityType(entity) || selectedEntityType || entityType;
    const label = getSingularLabel(entityType) || entityType;
    const badgeStyle = SUBTYPE_BADGE_STYLES[resolvedType.toLowerCase()] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    const showTypeBadge = isPerson ? !selectedEntityType : true;

    const email = entity?.Email || entity?.rawData?.Email || '';
    const upn = entity?.UserPrincipalName || entity?.Username || entity?.rawData?.UserPrincipalName || '';
    const department = entity?.rawData?.Department || entity?.rawData?.JobTitle || '';
    const gradeLevel = entity?.rawData?.GradeLevel || '';
    const jobCode = entity?.JobCode || entity?.rawData?.JobCode || '';
    const description = entity?.Description || entity?.rawData?.Description || '';

    const isFieldVisible = (key: string) => !visibleFields || visibleFields.has(key.toLowerCase());

    useEffect(() => {
        if (!isPerson || !entityId) {
            setPhotoLoaded(true);
            return;
        }

        const cached = photoService.getCachedPhoto(entityId);
        if (cached?.Photo) {
            setPhotoData(cached.Photo);
            setPhotoLoaded(true);
            return;
        }

        const photoRef = entity?.rawData?.Photo?.$ref || entity?.rawData?.Photo?.['$ref'] || null;
        if (!photoRef) {
            setPhotoLoaded(true);
            return;
        }

        let cancelled = false;
        photoService.getPhotoFromRef(photoRef)
            .then((resp) => {
                if (!cancelled && resp.Photo) {
                    setPhotoData(resp.Photo);
                    photoService.cachePhoto(entityId, resp.Photo);
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setPhotoLoaded(true); });
        return () => { cancelled = true; };
    }, [entityId, isPerson, entity?.rawData?.Photo]);

    const { Icon } = getEntityIconAndColor(entityType);

    const metaItems: string[] = [];
    if (entityId) metaItems.push(entityId);
    if (isPerson) {
        if (department && isFieldVisible('department')) metaItems.push(department);
        else if (jobCode && isFieldVisible('jobcode')) metaItems.push(jobCode);
        else if (gradeLevel && isFieldVisible('gradelevel')) metaItems.push(`Grade ${gradeLevel}`);
    }

    return (
        <div
            className={cn(
                'group flex items-start gap-3.5 p-4 rounded-lg border border-border/60',
                'bg-card hover:bg-muted/50 hover:border-border hover:shadow-sm',
                'cursor-pointer transition-all duration-150 ease-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            onClick={() => onSelect(entity)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(entity); } }}
            tabIndex={0}
            role="button"
            aria-label={`View ${displayName}`}
        >
            {/* Avatar */}
            <Avatar className={cn('h-12 w-12 shrink-0', !photoData && palette.bg)}>
                {isPerson && photoData ? (
                    <AvatarImage src={`data:image/jpeg;base64,${photoData}`} alt={displayName} />
                ) : null}
                <AvatarFallback className={cn(
                    'text-sm font-semibold',
                    isPerson ? palette.bg : 'bg-muted',
                    isPerson ? palette.text : 'text-muted-foreground',
                )}>
                    {isPerson ? initials : <Icon size={20} />}
                </AvatarFallback>
            </Avatar>

            {/* Content */}
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                {/* Name row */}
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-foreground truncate leading-tight" title={displayName}>
                        {displayName}
                    </span>
                    {showTypeBadge && (
                        <span className={cn(
                            'inline-flex shrink-0 items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none',
                            badgeStyle,
                        )}>
                            {isPerson ? resolvedType : label}
                        </span>
                    )}
                </div>

                {/* Email / description */}
                {isPerson && email && isFieldVisible('email') && (
                    <span className="text-xs text-muted-foreground truncate" title={email}>
                        {email}
                    </span>
                )}
                {!isPerson && description && (
                    <span className="text-xs text-muted-foreground truncate" title={description}>
                        {description}
                    </span>
                )}

                {/* Extra fields (generic entities) */}
                {!isPerson && fields && fields.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {fields.map((field, i) => {
                            if (!field.value) return null;
                            if (field.variant === 'badge') {
                                return (
                                    <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
                                        {field.value}
                                    </span>
                                );
                            }
                            if (field.variant === 'mono') {
                                return (
                                    <span key={i} className="text-[11px] font-mono text-muted-foreground/70 truncate">
                                        {field.value}
                                    </span>
                                );
                            }
                            return (
                                <span key={i} className="text-[11px] text-muted-foreground truncate">
                                    <span className="font-medium">{field.label}: </span>{field.value}
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* Person badges row */}
                {isPerson && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {department && isFieldVisible('department') && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
                                {department}
                            </span>
                        )}
                        {gradeLevel && isFieldVisible('gradelevel') && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                                Grade {gradeLevel}
                            </span>
                        )}
                        {jobCode && isFieldVisible('jobcode') && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-700">
                                {jobCode}
                            </span>
                        )}
                    </div>
                )}

                {/* Meta row: ID + UPN */}
                <div className="flex items-center gap-1.5 mt-0.5">
                    {entityId && (
                        <span className="text-[11px] font-mono text-muted-foreground/60 truncate">
                            {entityId}
                        </span>
                    )}
                    {isPerson && upn && isFieldVisible('userprincipalname') && (
                        <>
                            {entityId && <span className="w-0.5 h-0.5 rounded-full bg-border shrink-0" />}
                            <span className="text-[11px] font-mono text-muted-foreground/60 truncate">
                                {upn}
                            </span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export { ShadcnEntityCard };
